// Home for the different task attempt data schemas

import { z } from "zod";
import { PhotoReviewAttemptType } from "@remi/enums/photo-review-attempt-type";
import { TaskType, TaskTypeValues } from "@remi/enums/task-type";
import { UploadCompletedWorkPhotosAttemptType } from "@remi/enums/upload-completed-work-photos-attempt-type";

import { AssessQaResultsTaskAttemptSchema } from "./assess-qa-results";
import { TaskAttemptType } from "./attempt-types";
import {
  type CertificateOfCompletionCompletedTaskAttempt,
  CertificateOfCompletionCompletedTaskAttemptFormSchema,
  CertificateOfCompletionCompletedTaskAttemptSchema,
} from "./certificate-of-completion-completed";
import {
  type CollectHomeownerAvailabilityTaskAttempt,
  CollectHomeownerAvailabilityTaskAttemptSchema,
} from "./collect-homeowner-availability";
import {
  type ColorSelectionTaskAttempt,
  ColorSelectionTaskAttemptFormSchema,
  ColorSelectionTaskAttemptSchema,
} from "./color-selection";
import { ConfirmAppeasementPaymentTaskAttemptSchema } from "./confirm-appeasement-payment";
import { type ConfirmProjectIsRTITaskAttempt, ConfirmProjectIsRTITaskAttemptSchema } from "./confirm-project-is-rti";
import { type CreateInvoiceTaskAttempt, CreateInvoiceTaskAttemptSchema } from "./create-invoice";
import {
  CreateVendorCreditsSource,
  type CreateVendorCreditsTaskAttempt,
  CreateVendorCreditsTaskAttemptSchema,
} from "./create-vendor-credits";
import {
  type CreateWorkOrderTaskAttempt,
  type CreateWorkOrderTaskAttemptForm,
  CreateWorkOrderTaskAttemptFormSchema,
  CreateWorkOrderTaskAttemptSchema,
} from "./create-work-order";
import { FinalInspectionCompletedTaskAttemptSchema } from "./final-inspection-completed";
import {
  type FinalInspectionScheduledTaskAttempt,
  FinalInspectionScheduledTaskAttemptSchema,
} from "./final-inspection-scheduled";
import { FinalizeQuoteTaskAttemptSchema } from "./finalize-quote";
import { FORCE_COMPLETE_ATTEMPT_TYPE, ForceCompleteTaskAttemptSchema } from "./force-complete";

export { AssessQaResultsTaskAttemptSchema } from "./assess-qa-results";
export { FORCE_COMPLETE_ATTEMPT_TYPE } from "./force-complete";
export { SowChangeRequestTaskAttemptSchema } from "./sow-change-request";

import {
  type ApproveWorkOrderConcessionTaskAttempt,
  ApproveWorkOrderConcessionTaskAttemptSchema,
} from "./approve-work-order-concession";
import { InstallCompletedTaskAttemptSchema } from "./install-completed";
import { type InstallScheduledTaskAttempt, InstallScheduledTaskAttemptSchema } from "./install-scheduled";
import { InstallStartedTaskAttemptSchema } from "./install-started";
import {
  getApprovedCarrierDecision,
  type InsuranceApprovalReceivedTaskAttempt,
  InsuranceApprovalReceivedTaskAttemptSchema,
} from "./insurance-approval-received";
import { InsuranceElectiveEstimateSubmittedTaskAttemptSchema } from "./insurance-elective-estimate-submitted";
import {
  type InsuranceElectiveWorkCreatedTaskAttempt,
  InsuranceElectiveWorkCreatedTaskAttemptSchema,
} from "./insurance-elective-work-created";
import {
  type InsuranceEstimateCreatedTaskAttempt,
  InsuranceEstimateCreatedTaskAttemptSchema,
} from "./insurance-estimate-created";
import { InsuranceEstimateSubmittedTaskAttemptSchema } from "./insurance-estimate-submitted";
import {
  type InsuranceRevisionElectiveEstimateCreatedTaskAttempt,
  InsuranceRevisionElectiveEstimateCreatedTaskAttemptSchema,
} from "./insurance-revision-elective-estimate-created";
import { InsuranceRevisionElectiveEstimateSubmittedTaskAttemptSchema } from "./insurance-revision-elective-estimate-submitted";
import {
  type InsuranceRevisionEstimateCreatedTaskAttempt,
  InsuranceRevisionEstimateCreatedTaskAttemptSchema,
} from "./insurance-revision-estimate-created";
import { InsuranceRevisionEstimateSubmittedTaskAttemptSchema } from "./insurance-revision-estimate-submitted";
import { type IntroCallCompletedTaskAttempt, IntroCallCompletedTaskAttemptSchema } from "./intro-call-completed";
import { ItelReceivedTaskAttemptSchema } from "./itel-received";
import {
  type NoticeToProceedTaskAttempt,
  NoticeToProceedTaskAttemptFormSchema,
  NoticeToProceedTaskAttemptSchema,
} from "./notice-to-proceed";
import { OrderMaterialsTaskAttemptDatabaseSchema, OrderMaterialsTaskAttemptSchema } from "./order-materials";
import {
  PendingEstimateAssetsTaskAttemptDatabaseSchema,
  PendingEstimateAssetsTaskAttemptSchema,
} from "./pending-estimate-assets";
import { PermitCompletedTaskAttemptSchema } from "./permit-completed";
import {
  PropertyModelDetailAddedTaskAttemptFormSchema,
  PropertyModelDetailAddedTaskAttemptSchema,
} from "./property-model-detail-added";
import { type QuoteCreatedTaskAttempt, QuoteCreatedTaskAttemptSchema } from "./quote-created";
import { ReceiveRoofItelTaskAttemptSchema } from "./receive-roof-itel";
import { ReceiveSidingItelTaskAttemptSchema } from "./receive-siding-itel";
import {
  type RecordUpsellResultsInput,
  RecordUpsellResultsNewMaterialOptions,
  type RecordUpsellResultsTaskAttempt,
  type RecordUpsellResultsTaskAttemptFormData,
  RecordUpsellResultsTaskHistoryFormSchema,
  RecordUpsellResultsTaskHistorySchema,
  type RecordUpsellResultsTaskRequestFormData,
} from "./record-upsell-results";
import { RedesignReceivedTaskAttemptSchema } from "./redesign-received";
import {
  type ReviewAndAcceptWorkOrderTaskAttempt,
  ReviewAndAcceptWorkOrderTaskAttemptSchema,
} from "./review-and-accept-work-order";
import { ReviewAppeasementTaskAttemptSchema } from "./review-appeasement";
import { type ReviewRevenueTaskAttempt, ReviewRevenueTaskAttemptSchema } from "./review-revenue";
import {
  type ReviewWorkOrderRejectionTaskAttempt,
  ReviewWorkOrderRejectionTaskAttemptSchema,
} from "./review-work-order-rejection";
import { ScopeSelectedTaskAttemptSchema } from "./scope-selected";
import { SelectPvSystemsForRedesignTaskAttemptSchema } from "./select-pv-systems-for-redesign";
import { SendAppeasementTaskAttemptSchema } from "./send-appeasement";
import {
  type SiteVisitDetailsRecordedTaskAttempt,
  SiteVisitDetailsRecordedTaskAttemptSchema,
} from "./site-visit-details-recorded";
import {
  type SiteVisitResultsAnalyzedTaskAttempt,
  SiteVisitResultsAnalyzedTaskAttemptSchema,
} from "./site-visit-results-analyzed";
import { SowChangeFinalizeQuoteTaskAttemptSchema } from "./sow-change-finalize-quote";
import { SowChangeRequestTaskAttemptSchema } from "./sow-change-request";
import { SowChangeReviewTaskAttemptSchema } from "./sow-change-review";
import { SowChangeSendAddendumTaskAttemptSchema } from "./sow-change-send-addendum";
import { SowChangeVerifyTaskAttemptSchema } from "./sow-change-verify";
import { SubmitRoofItelTaskAttemptSchema } from "./submit-roof-itel";
import { SubmitSidingItelTaskAttemptSchema } from "./submit-siding-itel";
import { FindingsApprovedTaskAttemptSchema } from "./sunrun-findings-approved";
import { InvoiceApprovedTaskAttemptSchema } from "./sunrun-invoice-approved";
import { type SubmitFindingsTaskAttempt, SubmitFindingsTaskAttemptSchema } from "./sunrun-submit-findings";
import { WorkOrderApprovedTaskAttemptSchema } from "./sunrun-work-order-approved";
import {
  type UploadedCompletedWorkPhotosTaskAttempt,
  UploadedCompletedWorkPhotosTaskAttemptSchema,
} from "./uploaded-completed-work-photos";

export const TaskTypesWithHistoryUi = [
  TaskType.CERTIFICATE_OF_COMPLETION_COMPLETED,
  TaskType.CREATE_WORK_ORDER,
  TaskType.INSTALL_SCHEDULED,
  TaskType.FINAL_INSPECTION_SCHEDULED,
  TaskType.RECORD_UPSELL_RESULTS,
  TaskType.NOTICE_TO_PROCEED,
  TaskType.REVIEW_AND_ACCEPT_WORK_ORDER,
  TaskType.REVIEW_WORK_ORDER_REJECTION,
  TaskType.APPROVE_WORK_ORDER_CONCESSION,
  TaskType.CONFIRM_PROJECT_IS_RTI,
  TaskType.UPLOADED_COMPLETED_WORK_PHOTOS,
  TaskType.INSURANCE_ESTIMATE_CREATED,
  TaskType.INSURANCE_APPROVAL_RECEIVED,
  TaskType.INSURANCE_ELECTIVE_WORK_CREATED,
  TaskType.INSURANCE_ESTIMATE_SUBMITTED,
  TaskType.INSURANCE_ELECTIVE_ESTIMATE_SUBMITTED,
  TaskType.INSURANCE_REVISION_ESTIMATE_CREATED,
  TaskType.INSURANCE_REVISION_ESTIMATE_SUBMITTED,
  TaskType.INSURANCE_REVISION_ELECTIVE_ESTIMATE_CREATED,
  TaskType.INSURANCE_REVISION_ELECTIVE_ESTIMATE_SUBMITTED,
  TaskType.SITE_VISIT_RESULTS_ANALYZED,
  TaskType.SITE_VISIT_DETAILS_RECORDED,
  TaskType.COLLECT_HOMEOWNER_AVAILABILITY,
  TaskType.SUNRUN_SUBMIT_FINDINGS,
  TaskType.SUNRUN_FINDINGS_APPROVED,
  TaskType.SUNRUN_INVOICE_APPROVED,
  TaskType.SUNRUN_WORK_ORDER_APPROVED,
] as const satisfies TaskType[];

// These schemas are used to validate the data that we store on a task attempt row
// Discriminated union based on the task type that the attempt is used for

/**
 * Used to parse data sent from the client to the server
 */
export const TaskAttemptFormDataSchema = z.discriminatedUnion("type", [
  CertificateOfCompletionCompletedTaskAttemptFormSchema,
  CollectHomeownerAvailabilityTaskAttemptSchema,
  ColorSelectionTaskAttemptFormSchema,
  ConfirmAppeasementPaymentTaskAttemptSchema,
  ConfirmProjectIsRTITaskAttemptSchema,
  CreateInvoiceTaskAttemptSchema,
  CreateVendorCreditsTaskAttemptSchema,
  CreateWorkOrderTaskAttemptFormSchema,
  FinalInspectionCompletedTaskAttemptSchema,
  FinalInspectionScheduledTaskAttemptSchema,
  FinalizeQuoteTaskAttemptSchema,
  InstallCompletedTaskAttemptSchema,
  InstallScheduledTaskAttemptSchema,
  InstallStartedTaskAttemptSchema,
  IntroCallCompletedTaskAttemptSchema,
  ItelReceivedTaskAttemptSchema,
  NoticeToProceedTaskAttemptFormSchema,
  OrderMaterialsTaskAttemptSchema,
  PendingEstimateAssetsTaskAttemptSchema,
  PermitCompletedTaskAttemptSchema,
  PropertyModelDetailAddedTaskAttemptFormSchema,
  QuoteCreatedTaskAttemptSchema,
  RecordUpsellResultsTaskHistoryFormSchema,
  ReviewAndAcceptWorkOrderTaskAttemptSchema,
  ReviewAppeasementTaskAttemptSchema,
  ReviewRevenueTaskAttemptSchema,
  ScopeSelectedTaskAttemptSchema,
  SendAppeasementTaskAttemptSchema,
  UploadedCompletedWorkPhotosTaskAttemptSchema,
  InsuranceEstimateCreatedTaskAttemptSchema,
  InsuranceApprovalReceivedTaskAttemptSchema,
  InsuranceElectiveWorkCreatedTaskAttemptSchema,
  InsuranceEstimateSubmittedTaskAttemptSchema,
  InsuranceElectiveEstimateSubmittedTaskAttemptSchema,
  InsuranceRevisionEstimateCreatedTaskAttemptSchema,
  InsuranceRevisionEstimateSubmittedTaskAttemptSchema,
  InsuranceRevisionElectiveEstimateCreatedTaskAttemptSchema,
  InsuranceRevisionElectiveEstimateSubmittedTaskAttemptSchema,
  SiteVisitResultsAnalyzedTaskAttemptSchema,
  SiteVisitDetailsRecordedTaskAttemptSchema,
  RedesignReceivedTaskAttemptSchema,
  SelectPvSystemsForRedesignTaskAttemptSchema,
  ReceiveRoofItelTaskAttemptSchema,
  ReceiveSidingItelTaskAttemptSchema,
  FindingsApprovedTaskAttemptSchema,
  InvoiceApprovedTaskAttemptSchema,
  WorkOrderApprovedTaskAttemptSchema,
  SubmitFindingsTaskAttemptSchema,
  SubmitRoofItelTaskAttemptSchema,
  SubmitSidingItelTaskAttemptSchema,
]);

const SchematizedTaskAttemptSchemas = [
  // Deliberately absent from TaskAttemptFormDataSchema: that union's `type` keys the exhaustive
  // TaskAttemptHandlerMap, and ASSESS_QA_RESULTS is driven by its own mutation, not the attempt path.
  AssessQaResultsTaskAttemptSchema,
  CertificateOfCompletionCompletedTaskAttemptSchema,
  SowChangeRequestTaskAttemptSchema,
  SowChangeReviewTaskAttemptSchema,
  SowChangeFinalizeQuoteTaskAttemptSchema,
  SowChangeSendAddendumTaskAttemptSchema,
  SowChangeVerifyTaskAttemptSchema,
  CollectHomeownerAvailabilityTaskAttemptSchema,
  ColorSelectionTaskAttemptSchema,
  ConfirmAppeasementPaymentTaskAttemptSchema,
  ConfirmProjectIsRTITaskAttemptSchema,
  CreateInvoiceTaskAttemptSchema,
  CreateVendorCreditsTaskAttemptSchema,
  CreateWorkOrderTaskAttemptSchema,
  FinalInspectionCompletedTaskAttemptSchema,
  FinalInspectionScheduledTaskAttemptSchema,
  FinalizeQuoteTaskAttemptSchema,
  InstallCompletedTaskAttemptSchema,
  InstallScheduledTaskAttemptSchema,
  InstallStartedTaskAttemptSchema,
  IntroCallCompletedTaskAttemptSchema,
  ItelReceivedTaskAttemptSchema,
  NoticeToProceedTaskAttemptSchema,
  OrderMaterialsTaskAttemptDatabaseSchema,
  PendingEstimateAssetsTaskAttemptDatabaseSchema,
  PermitCompletedTaskAttemptSchema,
  PropertyModelDetailAddedTaskAttemptSchema,
  QuoteCreatedTaskAttemptSchema,
  RecordUpsellResultsTaskHistorySchema,
  ReviewAndAcceptWorkOrderTaskAttemptSchema,
  ReviewWorkOrderRejectionTaskAttemptSchema,
  ApproveWorkOrderConcessionTaskAttemptSchema,
  ReviewAppeasementTaskAttemptSchema,
  ReviewRevenueTaskAttemptSchema,
  ScopeSelectedTaskAttemptSchema,
  SendAppeasementTaskAttemptSchema,
  UploadedCompletedWorkPhotosTaskAttemptSchema,
  InsuranceEstimateCreatedTaskAttemptSchema,
  InsuranceApprovalReceivedTaskAttemptSchema,
  InsuranceElectiveWorkCreatedTaskAttemptSchema,
  InsuranceEstimateSubmittedTaskAttemptSchema,
  InsuranceElectiveEstimateSubmittedTaskAttemptSchema,
  InsuranceRevisionEstimateCreatedTaskAttemptSchema,
  InsuranceRevisionEstimateSubmittedTaskAttemptSchema,
  InsuranceRevisionElectiveEstimateCreatedTaskAttemptSchema,
  InsuranceRevisionElectiveEstimateSubmittedTaskAttemptSchema,
  SiteVisitResultsAnalyzedTaskAttemptSchema,
  SiteVisitDetailsRecordedTaskAttemptSchema,
  RedesignReceivedTaskAttemptSchema,
  SelectPvSystemsForRedesignTaskAttemptSchema,
  ReceiveRoofItelTaskAttemptSchema,
  ReceiveSidingItelTaskAttemptSchema,
  FindingsApprovedTaskAttemptSchema,
  InvoiceApprovedTaskAttemptSchema,
  WorkOrderApprovedTaskAttemptSchema,
  SubmitFindingsTaskAttemptSchema,
  SubmitRoofItelTaskAttemptSchema,
  SubmitSidingItelTaskAttemptSchema,
] as const;

type SchematizedAttemptTaskType = z.infer<(typeof SchematizedTaskAttemptSchemas)[number]>["type"];

export type DataLessAttemptTaskType = Exclude<TaskType, SchematizedAttemptTaskType>;

const schematizedAttemptTypes = new Set<TaskType>(
  SchematizedTaskAttemptSchemas.flatMap((s) =>
    "shape" in s ? [s.shape.type.value] : s.options.map((o) => o.shape.type.value),
  ),
);

const schematizedAttemptTypeValues = TaskTypeValues.filter((t): t is SchematizedAttemptTaskType =>
  schematizedAttemptTypes.has(t),
);

export function isDataLessAttemptTaskType(taskType: TaskType): taskType is DataLessAttemptTaskType {
  return !schematizedAttemptTypes.has(taskType);
}

/**
 * Used to parse data stored in the database. Task types without an explicit schema above validate
 * through the trailing catch-all as a bare `{ type }` (`.loose()` keeps any legacy extra fields so
 * the schema-verification gut check can still see structural anomalies rather than silently stripping them).
 */
export const TaskAttemptDataSchema = z.discriminatedUnion("type", [
  ...SchematizedTaskAttemptSchemas,
  ForceCompleteTaskAttemptSchema,
  z.object({ type: z.enum(TaskTypeValues).exclude(schematizedAttemptTypeValues) }).loose(),
]);

export const TaskAttemptInputSchema = z.object({
  taskId: z.string(),
  taskAttemptData: TaskAttemptFormDataSchema,
});

export type TaskAttemptInput = z.infer<typeof TaskAttemptInputSchema>;

export type TaskAttemptFormData = z.infer<typeof TaskAttemptFormDataSchema>;

export type TaskAttemptData = z.infer<typeof TaskAttemptDataSchema>;

export type TaskTypeWithAttemptData = TaskAttemptFormData["type"];

export type TaskAttemptDataByTaskType<T extends TaskTypeWithAttemptData> = Extract<TaskAttemptFormData, { type: T }>;

const RECORDS_ONLY_ATTEMPT_TYPES = new Set<TaskType>([
  TaskType.ASSESS_QA_RESULTS,
  TaskType.CERTIFICATE_OF_COMPLETION_COMPLETED,
  TaskType.CONFIRM_APPEASEMENT_PAYMENT,
  TaskType.CREATE_VENDOR_CREDITS,
  TaskType.FINALIZE_QUOTE,
  TaskType.SOW_CHANGE_FINALIZE_QUOTE,
  TaskType.SOW_CHANGE_SEND_ADDENDUM,
  TaskType.SUNRUN_FINDINGS_APPROVED,
  TaskType.SUNRUN_INVOICE_APPROVED,
  TaskType.QUOTE_CREATED,
  TaskType.REVIEW_APPEASEMENT,
  TaskType.REVIEW_WORK_ORDER_REJECTION,
  TaskType.APPROVE_WORK_ORDER_CONCESSION,
  TaskType.SEND_APPEASEMENT,
  TaskType.SUNRUN_SUBMIT_FINDINGS,
  TaskType.SUNRUN_WORK_ORDER_APPROVED,
]);

export function attemptCompletesTask(data: TaskAttemptData): boolean {
  if (data.type === FORCE_COMPLETE_ATTEMPT_TYPE) {
    return true;
  }
  if (isDataLessAttemptTaskType(data.type)) {
    return true;
  }
  if (RECORDS_ONLY_ATTEMPT_TYPES.has(data.type)) {
    return false;
  }
  switch (data.type) {
    case TaskType.INSTALL_SCHEDULED:
      return data.installScheduledAt !== null;
    case TaskType.FINAL_INSPECTION_SCHEDULED:
      return data.finalInspectionScheduledAt !== null;
    case TaskType.CONFIRM_PROJECT_IS_RTI:
      return data.attemptType === PhotoReviewAttemptType.ACCEPT;
    case TaskType.INSURANCE_APPROVAL_RECEIVED:
      return getApprovedCarrierDecision(data.data).approved;
    case TaskType.INSURANCE_ESTIMATE_SUBMITTED:
    case TaskType.INSURANCE_ELECTIVE_ESTIMATE_SUBMITTED:
    case TaskType.INSURANCE_REVISION_ESTIMATE_SUBMITTED:
    case TaskType.INSURANCE_REVISION_ELECTIVE_ESTIMATE_SUBMITTED:
      return data.data.payload.attemptType === "ACCEPTED";
    case TaskType.REVIEW_AND_ACCEPT_WORK_ORDER:
      return data.payload.attemptType === "ACCEPT";
    case TaskType.RECORD_UPSELL_RESULTS:
      return data.payload.attemptType === TaskAttemptType.ATTEMPT;
    case TaskType.COLLECT_HOMEOWNER_AVAILABILITY:
      return data.selectedSlots.length > 0;
    case TaskType.UPLOADED_COMPLETED_WORK_PHOTOS:
      return data.attemptType === UploadCompletedWorkPhotosAttemptType.UPLOAD;
    default:
      return true;
  }
}

export type {
  ApproveWorkOrderConcessionTaskAttempt,
  CertificateOfCompletionCompletedTaskAttempt,
  CollectHomeownerAvailabilityTaskAttempt,
  ColorSelectionTaskAttempt,
  ConfirmProjectIsRTITaskAttempt,
  CreateInvoiceTaskAttempt,
  CreateVendorCreditsTaskAttempt,
  CreateWorkOrderTaskAttempt,
  CreateWorkOrderTaskAttemptForm,
  FinalInspectionScheduledTaskAttempt,
  InstallScheduledTaskAttempt,
  InsuranceApprovalReceivedTaskAttempt,
  InsuranceElectiveWorkCreatedTaskAttempt,
  InsuranceEstimateCreatedTaskAttempt,
  InsuranceRevisionElectiveEstimateCreatedTaskAttempt,
  InsuranceRevisionEstimateCreatedTaskAttempt,
  IntroCallCompletedTaskAttempt,
  NoticeToProceedTaskAttempt,
  QuoteCreatedTaskAttempt,
  RecordUpsellResultsInput,
  RecordUpsellResultsTaskAttempt,
  RecordUpsellResultsTaskAttemptFormData,
  RecordUpsellResultsTaskRequestFormData,
  ReviewAndAcceptWorkOrderTaskAttempt,
  ReviewRevenueTaskAttempt,
  ReviewWorkOrderRejectionTaskAttempt,
  SiteVisitDetailsRecordedTaskAttempt,
  SiteVisitResultsAnalyzedTaskAttempt,
  SubmitFindingsTaskAttempt,
  UploadedCompletedWorkPhotosTaskAttempt,
};
// Export each task type's schema
export {
  CollectHomeownerAvailabilityTaskAttemptSchema,
  CreateVendorCreditsSource,
  CreateVendorCreditsTaskAttemptSchema,
  CreateWorkOrderTaskAttemptSchema,
  FinalizeQuoteTaskAttemptSchema,
  getApprovedCarrierDecision,
  PermitCompletedTaskAttemptSchema,
  QuoteCreatedTaskAttemptSchema,
  RecordUpsellResultsNewMaterialOptions,
  RecordUpsellResultsTaskHistoryFormSchema,
  ReviewRevenueTaskAttemptSchema,
  SelectPvSystemsForRedesignTaskAttemptSchema,
  TaskAttemptType,
};

/**
 * Filters attempts to a single type, preserving input order. Attempt lists from the task-attempt repos
 * are oldest-first, so the last element of the result is the most recent attempt of that type.
 */
export function filterAttemptsOfType<T extends TaskType, A extends { data: TaskAttemptData }>(
  attempts: A[],
  type: T,
): (A & { data: Extract<TaskAttemptData, { type: T }> })[] {
  return attempts.filter((a): a is A & { data: Extract<TaskAttemptData, { type: T }> } => a.data.type === type);
}

interface SubmissionOrderKey {
  id: string;
  createdAt: Date;
}

// TaskEvent ids are cuids, not sequential: the id only makes equal-createdAt ties deterministic.
export function compareSubmissionsNewestFirst(a: SubmissionOrderKey, b: SubmissionOrderKey): number {
  return b.createdAt.getTime() - a.createdAt.getTime() || b.id.localeCompare(a.id);
}

export type { EstimateCreatedV1Data, EstimateSubmittedV1Data } from "./estimate-review-shared";
// These are additional types that need to be exported. These are nested types from versioned schemas.
export type {
  InsuranceApprovalReceivedV1ApprovedData,
  InsuranceApprovalReceivedV1Data,
  InsuranceApprovalReceivedV1RejectedData,
} from "./insurance-approval-received";
export type { InsuranceElectiveWorkCreatedV1Data } from "./insurance-elective-work-created";
export type { InsuranceEstimateCreatedV1Data } from "./insurance-estimate-created";
